'use client';

// Generic phase-aware progress chip used by:
//   - <AiStreamStatusChip> (SSE generate flow — store-driven phase)
//   - <SuggestOutcomesButton> (REST chain — local-state driven)
// Pure presentational — consumer derives `mode` (progress/done/error/hidden)
// and the `phase` string from its own state. Phase strings map directly to
// `aiProposal.streaming.<phase>` i18n keys so both surfaces share copy.
import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { Alert01Icon, Loading03Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import type { AiErrorCode } from '@/stores/proposal-form';
const FADE_INITIAL = {
  opacity: 0,
  y: -2
} as const;
const FADE_ANIMATE = {
  opacity: 1,
  y: 0
} as const;
const FADE_EXIT = {
  opacity: 0,
  y: -2
} as const;
export type AiPhase = 'starting' | 'writingMarket' | 'draftingTitle' | 'writingDescription' | 'definingOutcomes' | 'findingImages' | 'finalizingImages' | 'cancelling' | 'readyToCreate' | 'finalizationFailed' | 'draftingLabel' | 'pickingTicker' | 'findingImage';
export type AiProgressMode = 'progress' | 'done' | 'error' | 'hidden';
export interface AiProgressChipProps {
  mode: AiProgressMode;
  /** Required when mode === 'progress'. Resolves to `aiProposal.streaming.<phase>`. */
  phase?: AiPhase;
  errorCode?: AiErrorCode;
  /** When phase === 'findingImages' AND > 0, drives the (X/N) count + progress bar. */
  imagesExpected?: number;
  imagesSettled?: number;
}
export function AiProgressChip(props) {
  const $ = _c(46);
  const t = useTranslations("aiProposal.streaming");
  const reduce = useReducedMotion();
  if (props.mode === "hidden") {
    return null;
  }
  let t0;
  if ($[0] !== reduce) {
    t0 = reduce === true ? {
      duration: 0
    } : MOTION_ENTRANCE;
    $[0] = reduce;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const transition = t0;
  const initial = reduce === true ? false : FADE_INITIAL;
  if (props.mode === "error") {
    let t1;
    if ($[2] !== props.errorCode || $[3] !== props.phase || $[4] !== t) {
      t1 = props.errorCode === "ai_market_content_policy" ? t("contentPolicy") : props.phase === "finalizationFailed" ? t("finalizationFailed") : t("failed");
      $[2] = props.errorCode;
      $[3] = props.phase;
      $[4] = t;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    const errorLabel = t1;
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Icon icon={Alert01Icon} className="size-4 shrink-0 text-feedback-error-content" aria-hidden="true" />;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== errorLabel) {
      t3 = <div role="alert" className="overflow-hidden rounded-card border border-feedback-error-border/40 bg-feedback-error-accent/10"><div className="flex items-center gap-2.5 px-3 py-2">{t2}<span className="flex-1 text-body font-medium text-foreground-primary">{errorLabel}</span></div></div>;
      $[7] = errorLabel;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (props.mode === "done") {
    let t1;
    if ($[9] !== props.phase || $[10] !== t) {
      t1 = props.phase === "readyToCreate" ? t("readyToCreate") : t("allSet");
      $[9] = props.phase;
      $[10] = t;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    const doneLabel = t1;
    let t2;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Icon icon={Tick02Icon} className="size-4 shrink-0 text-feedback-success-content" aria-hidden="true" />;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== doneLabel) {
      t3 = <output className="block overflow-hidden rounded-card border border-feedback-success-border/40 bg-feedback-success-accent/10"><div className="flex items-center gap-2.5 px-3 py-2">{t2}<span className="flex-1 text-body font-medium text-foreground-primary">{doneLabel}</span></div></output>;
      $[13] = doneLabel;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    return t3;
  }
  const phase = props.phase ?? "starting";
  const imagesExpected = props.imagesExpected ?? 0;
  const imagesSettled = Math.min(props.imagesSettled ?? 0, imagesExpected);
  const showCount = phase === "findingImages" && imagesExpected > 0;
  const imagesProgressPct = showCount ? imagesSettled / imagesExpected * 100 : 0;
  let label;
  let t1;
  if ($[15] !== imagesExpected || $[16] !== imagesSettled || $[17] !== phase || $[18] !== showCount || $[19] !== t) {
    let t2;
    bb0: {
      if (showCount) {
        t2 = t("findingImages", {
          settled: imagesSettled,
          total: imagesExpected
        });
        break bb0;
      }
      switch (phase) {
        case "starting":
          {
            t2 = t("starting");
            break bb0;
          }
        case "writingMarket":
          {
            t2 = t("writingMarket");
            break bb0;
          }
        case "draftingTitle":
          {
            t2 = t("draftingTitle");
            break bb0;
          }
        case "writingDescription":
          {
            t2 = t("writingDescription");
            break bb0;
          }
        case "definingOutcomes":
          {
            t2 = t("definingOutcomes");
            break bb0;
          }
        case "findingImages":
          {
            t2 = t("findingImages", {
              settled: imagesSettled,
              total: imagesExpected
            });
            break bb0;
          }
        case "finalizingImages":
          {
            t2 = t("finalizingImages");
            break bb0;
          }
        case "cancelling":
          {
            t2 = t("cancelling");
            break bb0;
          }
        case "draftingLabel":
          {
            t2 = t("draftingLabel");
            break bb0;
          }
        case "pickingTicker":
          {
            t2 = t("pickingTicker");
            break bb0;
          }
        case "findingImage":
          {
            t2 = t("findingImage");
            break bb0;
          }
        case "readyToCreate":
          {
            t2 = t("readyToCreate");
            break bb0;
          }
        case "finalizationFailed":
          {
            t2 = t("finalizationFailed");
            break bb0;
          }
      }
      t2 = undefined;
    }
    label = t2;
    t1 = showCount ? label.replace(/\s*\([^)]*\)\s*$/, "") : label;
    $[15] = imagesExpected;
    $[16] = imagesSettled;
    $[17] = phase;
    $[18] = showCount;
    $[19] = t;
    $[20] = label;
    $[21] = t1;
  } else {
    label = $[20];
    t1 = $[21];
  }
  const baseLabel = t1;
  let t2;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={Loading03Icon} className="size-4 shrink-0 animate-spin text-foreground-brand motion-reduce:animate-none" aria-hidden="true" />;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== baseLabel || $[24] !== initial || $[25] !== phase || $[26] !== transition) {
    t3 = <AnimatePresence mode="wait"><m.span key={phase} initial={initial} animate={FADE_ANIMATE} exit={FADE_EXIT} transition={transition} className="flex-1 text-body font-medium text-foreground-primary">{baseLabel}</m.span></AnimatePresence>;
    $[23] = baseLabel;
    $[24] = initial;
    $[25] = phase;
    $[26] = transition;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  let t4;
  if ($[28] !== imagesExpected || $[29] !== imagesSettled || $[30] !== label || $[31] !== showCount) {
    t4 = showCount ? <span role="img" aria-label={label} className="flex shrink-0 items-center text-caption tabular-nums text-foreground-secondary"><AnimatedNumber value={imagesSettled} format={formatInt} duration={300} /><span className="mx-0.5 text-foreground-tertiary">/</span><span>{imagesExpected}</span></span> : null;
    $[28] = imagesExpected;
    $[29] = imagesSettled;
    $[30] = label;
    $[31] = showCount;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== t3 || $[34] !== t4) {
    t5 = <div className="flex items-center gap-2.5 px-3 py-2">{t2}{t3}{t4}</div>;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== imagesExpected || $[37] !== imagesProgressPct || $[38] !== imagesSettled || $[39] !== label || $[40] !== reduce || $[41] !== showCount) {
    t6 = showCount ? <div role="progressbar" aria-valuenow={imagesSettled} aria-valuemin={0} aria-valuemax={imagesExpected} aria-label={label} className="h-0.5 w-full bg-background-secondary"><m.div className="h-full w-full origin-left bg-foreground-brand" initial={false} animate={{
        scaleX: imagesProgressPct / 100
      }} transition={reduce === true ? {
        duration: 0
      } : {
        duration: 0.4,
        ease: "easeOut"
      }} /></div> : null;
    $[36] = imagesExpected;
    $[37] = imagesProgressPct;
    $[38] = imagesSettled;
    $[39] = label;
    $[40] = reduce;
    $[41] = showCount;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== t5 || $[44] !== t6) {
    t7 = <LazyMotion features={domAnimation}><div className="overflow-hidden rounded-card border bg-background-tertiary">{t5}{t6}</div></LazyMotion>;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  return t7;
}
function formatInt(n: number): string {
  return Math.round(n).toString();
}